/**
 * KAN-414 — modál při odchodu z konfigurátoru s NEPOTVRZENOU položkou.
 *
 * Od KAN-414 nahraný soubor v košíku není, dokud ho zákazník nepotvrdí
 * („Přidat do košíku"). Odejít ze stránky tedy nově znamená, že o
 * rozdělanou konfiguraci přijde — a to se nesmí stát potichu. Modál
 * proto nabízí obě cesty ven: zahodit, nebo přidat do košíku.
 *
 * Proč VLASTNÍ modál a ne ``window.confirm``: nativní dialog je v repu
 * zakázaný lintem (``no-restricted-syntax``), nedá se stylovat a hlavně
 * unese jen dvě volby — tady jsou tři (zůstat / zahodit / přidat).
 *
 * ``beforeunload`` je pod tím jen ZÁCHRANA pro zavření panelu nebo
 * reload: prohlížeč tam vlastní text ani tlačítka nepustí, takže
 * nahradit modál neumí. Pro navigaci uvnitř SPA se nepoužije vůbec.
 */

import { useEffect, useState, type JSX, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";

import { cx } from "@/lib/utils";

interface Props {
  /** Sdílený vypínač hlídání. Nastaví ho na ``true`` KAŽDÝ, kdo
   *  položku právě potvrdil nebo zahodil — modál i tlačítko „Přidat do
   *  košíku" v konfigurátoru.
   *
   *  Proč ref a ne stav: tlačítko po úspěšném uložení naviguje HNED
   *  (``onAdded``), tedy dřív, než by se stihl promítnout ``setState``.
   *  Blocker by tu navigaci zadržel a zákazníkovi, který právě přidal
   *  do košíku, by vyskočil modál s otázkou, jestli to chce přidat do
   *  košíku. Ref je čtený v okamžiku navigace, takže tenhle závod
   *  nemá. */
  disarmedRef: MutableRefObject<boolean>;
  /** Smí se z modálu potvrdit? Zrcadlí stav tlačítka „Přidat do
   *  košíku" — bez materiálu / s chybou ceny se potvrdit nedá, a
   *  nabízet nefunkční tlačítko je horší než ho nenabídnout. */
  canConfirm: boolean;
  /** Přidá položku do košíku. Musí doběhnout, než navigaci pustíme. */
  onConfirm: () => Promise<void>;
  /** Zahodí nepotvrzenou položku (DELETE /cart/items/{slug}). */
  onDiscard: () => Promise<void>;
}

type Pending = "confirm" | "discard" | null;

/**
 * ⚠️ Mountuj JEN když je co hlídat. ``useBlocker`` chce data router
 * (``createBrowserRouter``), takže bezpodmínečný mount by povinnost
 * mít ho přenesl na každé místo, kde se konfigurátor vykresluje —
 * včetně testů a projektové plochy, kde odkladiště neexistuje.
 */
export function UnconfirmedItemGuard({
  disarmedRef,
  canConfirm,
  onConfirm,
  onDiscard,
}: Props): JSX.Element | null {
  const { t } = useTranslation("quote");
  const [pending, setPending] = useState<Pending>(null);
  const [failed, setFailed] = useState(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !disarmedRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (disarmedRef.current) return;
      // Prohlížeč ukáže vlastní generickou hlášku; jediné, co po nás
      // chce, je preventDefault. Text sem nepatří — stejně ho zahodí.
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [disarmedRef]);

  if (blocker.state !== "blocked") return null;

  const run = async (which: Exclude<Pending, null>) => {
    setPending(which);
    setFailed(false);
    try {
      await (which === "confirm" ? onConfirm() : onDiscard());
    } catch {
      // Nepovedlo se → navigaci NEPOUŠTÍME. Pustit ji by znamenalo, že
      // zákazník klikl „Přidat do košíku", odešel a v košíku nic nemá.
      setPending(null);
      setFailed(true);
      return;
    }
    setPending(null);
    disarmedRef.current = true;
    blocker.proceed();
  };

  const busy = pending !== null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="unconfirmed-item-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4"
      data-testid="unconfirmed-item-dialog"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-5 shadow-xl">
        <h2
          id="unconfirmed-item-title"
          className="text-lg font-semibold text-ink"
        >
          {t("unconfirmed.title")}
        </h2>
        <p className="mt-2 text-sm text-ink-2">{t("unconfirmed.body")}</p>
        {failed && (
          <p
            className="mt-3 text-sm text-danger"
            role="alert"
            data-testid="unconfirmed-item-error"
          >
            {t("unconfirmed.failed")}
          </p>
        )}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => blocker.reset?.()}
            disabled={busy}
            className={cx(
              "rounded-md border border-border-strong bg-surface px-3 py-1.5 text-sm font-medium text-ink-2",
              "hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50",
            )}
            data-testid="unconfirmed-item-stay"
          >
            {t("unconfirmed.stay")}
          </button>
          <button
            type="button"
            onClick={() => void run("discard")}
            disabled={busy}
            className={cx(
              "rounded-md border border-border-strong bg-surface px-3 py-1.5 text-sm font-medium text-danger",
              "hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50",
            )}
            data-testid="unconfirmed-item-discard"
          >
            {t("unconfirmed.discard")}
          </button>
          {/* Nemá-li se co potvrdit (chybí materiál, cena selhala), se
              tlačítko nenabídne vůbec — zašedlé „Přidat do košíku"
              vedle „Zahodit" čte zákazník jako „ještě chvíli". */}
          {canConfirm && (
            <button
              type="button"
              onClick={() => void run("confirm")}
              disabled={busy}
              className={cx(
                "rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-bg",
                "hover:bg-accent-ink disabled:cursor-not-allowed disabled:opacity-50",
              )}
              data-testid="unconfirmed-item-confirm"
            >
              {t("unconfirmed.confirm")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
